/**
 * useConfirmDialog
 * Hook för att visa ConfirmDialog med imperativ API
 * Använd: const { confirm, ConfirmDialog } = useConfirmDialog();
 *        confirm({ title, message, onConfirm, variant });
 */

import { useState, useCallback } from 'react';
import ConfirmDialog from '@/components/common/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message: string;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export function useConfirmDialog() {
  const [state, setState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions | null;
  }>({ isOpen: false, options: null });

  const confirm = useCallback((options: ConfirmOptions) => {
    setState({ isOpen: true, options });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false, options: null }));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (state.options?.onConfirm) {
      await state.options.onConfirm();
    }
    close();
  }, [state.options, close]);

  const ConfirmDialogComponent = state.options ? (
    <ConfirmDialog
      isOpen={state.isOpen}
      onClose={close}
      onConfirm={handleConfirm}
      title={state.options.title}
      message={state.options.message}
      confirmText={state.options.confirmText}
      cancelText={state.options.cancelText}
      variant={state.options.variant}
    />
  ) : null;

  return { confirm, close, ConfirmDialog: () => ConfirmDialogComponent } as const;
}
